/* eslint-disable react/prop-types */
import { Suspense, lazy, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import "tippy.js/dist/tippy.css";
import { parallaxMouseMovement, useRellaxParallax } from "../../utils/parallax";
import { getInfo } from "../../utils/urlUtil";
import { SEO_MARKETS, getHreflang } from "../../constants/locales";
import { routeExistsInMarket, routeHasAlternates, withTrailingSlash } from "../../constants/seo";
import { motion } from "framer-motion";
import { SafeMotion } from "../common/SafeMotion";
import OfflineBanner from "../common/OfflineBanner";
import WhatsAppButton from "../common/WhatsAppButton";

const Footer = lazy(() => import("../footer/Footer"));
const Header = lazy(() => import("../header/Header"));

const Layout = ({ children }) => {
    const location = useLocation();
    const urlInfo = getInfo();
    const [isMobile, setIsMobile] = useState(false);
    const [navClasses, setNavClasses] = useState(`dark transparent`);
    useRellaxParallax();
    const [mobileClass, setMobileClass] = useState('');
    const [isFading, setIsFading] = useState(false);
    const onCompleteRef = useRef(null);

    function init_resize() {
        const _isMobile = window.innerWidth <= 1024
        setIsMobile(_isMobile)

        if (_isMobile) {
            setMobileClass("mobile-on");
        } else {
            setMobileClass("");
        }
    }

    useEffect(() => {
        // Only markets that actually have their own pages belong in hreflang.
        // Pointing hreflang at a country without a site sends Googlebot to
        // /gl/pagenotfound, which invalidates the whole alternates cluster.
        const base = 'https://www.rainbowagro.com';
        // Sin barra final para comparar rutas ("" es la home), con barra al
        // construir las URLs: Amplify sirve /tr/ y esa es la forma canónica.
        const path = location.pathname.replace(/^\/[a-z]{2}(-preview)?/, '').replace(/\/$/, '');
        document.querySelectorAll('link[data-hra]').forEach(l => l.remove());
        const frag = document.createDocumentFragment();
        const addAlternate = (hreflang, href) => {
            const link = document.createElement('link');
            link.rel = 'alternate';
            link.hreflang = hreflang;
            link.href = href;
            link.dataset.hra = '1';
            frag.appendChild(link);
        };
        // Self-referencing alternate included on purpose: Google requires every
        // page in the cluster to list itself.
        if (routeHasAlternates(path)) {
            SEO_MARKETS
                .filter(code => routeExistsInMarket(path, code))
                .forEach(code => addAlternate(getHreflang(code), withTrailingSlash(`${base}/${code}${path}`)));
            if (routeExistsInMarket(path, 'gl')) addAlternate('x-default', withTrailingSlash(`${base}/gl${path}`));
        }
        document.head.appendChild(frag);

        // Update canonical and og:url for current path
        const fullUrl = withTrailingSlash(`${base}${location.pathname}`);
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = fullUrl;
        let ogUrl = document.querySelector('meta[property="og:url"]');
        if (!ogUrl) {
            ogUrl = document.createElement('meta');
            ogUrl.setAttribute('property', 'og:url');
            document.head.appendChild(ogUrl);
        }
        ogUrl.content = fullUrl;

        return () => { document.querySelectorAll('link[data-hra]').forEach(l => l.remove()); };
    }, [location.pathname]);

    useEffect(() => {
        init_resize();

        // Lazy load CSS solo cuando se necesite
        import("swiper/css");
        import("swiper/css/effect-fade");
        import("jarallax/dist/jarallax.min.css");
        import("photoswipe/dist/photoswipe.css");
        import("react-modal-video/css/modal-video.css");

        window.addEventListener("resize", init_resize);
        const handler = (e) => {
            onCompleteRef.current = e.detail?.onComplete || null;
            setIsFading(true);
        };
        window.addEventListener("app:pageFadeOut", handler);

        return () => {
            window.removeEventListener("resize", init_resize);
            window.removeEventListener("app:pageFadeOut", handler);
        };
    }, []);

    useEffect(() => {
        const lightMenuSections = ['/serviceunits', '/news', '/contact-us', '/business-partners', '/bio-solutions', '/privacy-statement', '/terms-and-conditions', '/product-list', '/product', '/investors', `/reports`, '/custom-solutions', '/local-report'];
        let isLightMenuSection = lightMenuSections.some(section =>
            location.pathname.includes(section)
        );
        //para solutions si la pagina no es global tengo que tratar el menú como light
        if (location.pathname.includes('/solutions')) {
            const country = urlInfo.isoCountry;
            if (country == 'gl') {
                isLightMenuSection = false;
            } else {
                isLightMenuSection = true;
            }
        }

        // Keep hash navigation in control when a deep anchor is present.
        if (!location.hash) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
        setNavClasses(`${isLightMenuSection ? '' : 'dark'} transparent `);
        parallaxMouseMovement();

        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            // Actualiza las clases del nav según el valor de scrollY
            if (currentScrollY === 0) {
                setNavClasses(`${isLightMenuSection ? '' : 'dark'} transparent `);
            } else {
                setNavClasses("small-height body-scrolled ");
            }
        };

        // Escuchar el evento de scroll
        window.addEventListener("scroll", handleScroll);

        if (typeof window !== "undefined") {
            import("bootstrap/dist/js/bootstrap.esm");
        }

        // Limpia el listener al desmontar el componente
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return (
        <>
            <OfflineBanner />
            <div className="appear-animate body">
                <div className={`theme-main ${mobileClass}`}>
                    <div className="page" id="top">
                        <SafeMotion.nav
                            className={`main-nav light-after-scroll stick-fixed ${navClasses} `}
                            initial={{ opacity: 0, y: -30 }}
                            transition={{ duration: 1.5 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <Suspense>
                                <Header isMobile={isMobile} />
                            </Suspense>
                        </SafeMotion.nav>

                        <main id="main">
                            <SafeMotion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35 }}>
                                {children}
                            </SafeMotion.div>
                        </main>

                        <Suspense>
                            <Footer />
                        </Suspense>

                        <WhatsAppButton />

                        {isFading && (
                            <motion.div
                                key="page-fade-overlay"
                                className="fixed inset-0 z-[9999] pointer-events-none"
                                style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1, transition: { duration: 0.45, ease: "easeInOut" } }}
                                onAnimationComplete={() => {
                                    if (typeof onCompleteRef.current === "function") {
                                        onCompleteRef.current();
                                    }
                                }}
                            >
                                <div className="w-full h-full flex items-center justify-center" aria-hidden>
                                    <span className="text-white text-sm tracking-wide">…</span>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Layout;
